import React, { useEffect } from "react";
import TierListsGrid from "./TierListsGrid";
import TierListsTable from "./TierListsTable";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import TierListFilters from "./TierListFilters";
import { validParams } from "query_parameters/valid_parameters";
import { PVP_GAME_MODE_OPTIONS } from "query_parameters/filter_options";
import { useQueryString } from "hooks/general-hooks";
import { Helmet } from "react-helmet-async";
import {
  EXPANSION,
  getDifficultyPath,
  getFormattedContentType,
  getFormattedRole,
  getSeoContentEncounter,
  getSeoContentEncounterTwo,
  PATCH,
} from "../../../lib/wowHelpers";
import DataDetails from "../../Common/DataDetails";
import { DateTime } from "luxon";
import { getEncounterId } from "../../../hooks/path-hooks";
import { useLocation } from "react-router-dom";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import NoData from "../../Common/NoData";
import PvpTierListsTable from "./PVPTierListsTable";
import TierListCrossLink from "components/CrossLinks/TierListCrossLink";
import { TierListTableSkeleton } from "components/Common/PageSkeletons";
import Banner from "components/Common/Banner";
import { MediaQuery } from "@outplayed/responsive";
import { RampLeftRail, RampRightRail } from "components/Ads/RampRails";

function getPathInfoTierPage(path) {
  const pathArr = path.split("/");
  const game = pathArr[1]?.toLowerCase() ?? "";
  const page = pathArr[2]?.toLowerCase() ?? "";
  const tierListInfo = pathArr[3]?.toLowerCase() ?? "";
  let encounterType = pathArr[4]?.toLowerCase() ?? "";

  if (encounterType === "mythic+" || encounterType == "") {
    encounterType = "mythic";
  }

  return {
    game,
    page,
    tierListInfo,
    encounterType,
  };
}

function getTierListJsonDirectory(encounterType, encounterID, affix, gameMode, defaultParams, difficulty, hscombined) {
  let tierListJSON = "tierlist.json";
  if (encounterType === "mythic" && encounterID === "all" && affix === "all") {
    tierListJSON = "mythic-default-tierlist.json";
  }
  if (encounterType === "raid" && encounterID === "all") {
    tierListJSON = "raid-default-tierlist.json";
  }
  if (encounterType === "pvp") {
    const validGameMode = PVP_GAME_MODE_OPTIONS.some((option) => option.value === gameMode);
    tierListJSON = `PVP/${validGameMode ? gameMode : "3v3"}/tierlist.json`;
  }

  const difficultyPath = getDifficultyPath(difficulty || defaultParams.difficulty, encounterType);

  if (encounterType === "pvp") {
    return `${API_WOW_ROOT_PATH}/${tierListJSON}`;
  } else if (hscombined === "1") {
    return `${API_WOW_ROOT_PATH}/NHS/${difficultyPath}/${tierListJSON}`;
  } else {
    return `${API_WOW_ROOT_PATH}/${difficultyPath}/${tierListJSON}`;
  }
}

const TierListsContainer = () => {
  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  const validPageParams = validParams["tierList"] || {};
  const defaultParams = validPageParams.default ? { ...validPageParams.default } : { encounterType: "mythic" };

  const location = useLocation();
  let { tierListInfo, encounterType } = getPathInfoTierPage(location.pathname);

  const queryParams = useQueryString();
  let { difficulty, stat, gameMode, hscombined } = queryParams;
  const affix = "all";
  let role = "dps";

  if (!encounterType) {
    encounterType = "mythic";
  }
  if (!role) {
    role = "dps";
  }
  if (!stat) {
    stat = role === "heal" ? "hps" : "dps";
  }
  if (encounterType === "mythic" && !difficulty) {
    difficulty = "all";
  }
  const optionalParams = location.search.split("?");
  const encounterID = getEncounterId(optionalParams, encounterType);

  let tierListURLString = getTierListJsonDirectory(
    encounterType,
    encounterID,
    affix,
    gameMode,
    defaultParams,
    difficulty,
    hscombined,
  );

  const { data: tierList, loading: tierListIsLoading } = useJSONFetcher(tierListURLString, {
    ssr: true,
  });

  let data = [];
  let talents = [];
  let parses = 0;
  let hasData = false;
  let hasTalentsData = false;
  let roleAccessor;
  if (tierListInfo.includes("dps-rankings")) {
    roleAccessor = "dps_list";
    role = "dps";
  } else if (tierListInfo.includes("healer-rankings")) {
    roleAccessor = "healing_tier_list";
    role = "heal";
  } else if (tierListInfo.includes("tank-rankings")) {
    roleAccessor = "tank_tier_list";
    role = "tank";
  }

  if (tierList && encounterType && tierListInfo && encounterType !== "pvp") {
    if (encounterType === "raid") {
      if (encounterID === "all") {
        hasData = (tierList?.[encounterType]?.[roleAccessor]?.length ?? 0) > 0;
      } else {
        hasData = (tierList?.[encounterType]?.[encounterID]?.[roleAccessor]?.length ?? 0) > 0;
      }
    } else {
      if (affix === "all" && encounterID === "all") {
        hasData = (tierList?.[encounterType]?.[roleAccessor]?.length ?? 0) > 0;
      } else if (affix === "all") {
        hasData = (tierList?.[encounterType]?.[encounterID]?.[roleAccessor]?.length ?? 0) > 0;
      } else {
        hasData = (tierList?.[affix]?.[encounterID]?.[roleAccessor]?.length ?? 0) > 0;
      }
    }

    if (hasData) {
      if (encounterType === "raid") {
        if (encounterID === "all") {
          data = tierList?.[encounterType]?.[roleAccessor];
        } else {
          data = tierList?.[encounterType]?.[encounterID]?.[roleAccessor];
        }
      } else {
        if (affix === "all" && encounterID === "all") {
          data = tierList?.[encounterType]?.[roleAccessor];
        } else if (affix === "all") {
          data = tierList?.[encounterType]?.[encounterID]?.[roleAccessor];
        } else {
          data = tierList?.[affix]?.[encounterID]?.[roleAccessor];
        }
      }
    }

    hasTalentsData =
      affix && affix !== "all" ? tierList?.talents?.[affix]?.[encounterID] : tierList?.talents?.[encounterType]?.[encounterID];

    talents =
      hasTalentsData &&
      (affix && affix !== "all" ? tierList.talents[affix][encounterID] : tierList.talents[encounterType][encounterID]);
    parses = hasData ? data.reduce((acc, spec) => acc + spec.count, 0).toLocaleString("en-US") : 0;
    if (parses <= 0) hasData = false;
  } else if (tierList && encounterType && tierListInfo && encounterType === "pvp") {
    let tierListType = "dps";
    if (tierListInfo === "tank-rankings") {
      tierListType = "tanks";
    } else if (tierListInfo === "healer-rankings") {
      tierListType = "healers";
    }

    hasData = (tierList.tier_list?.[tierListType]?.length ?? 0) > 0;
    hasTalentsData = "talents" in tierList;
    data = hasData && tierList["tier_list"][tierListType];
    talents = hasTalentsData && tierList["talents"];
    parses = hasData && tierList["tier_list"][tierListType][0].total_games_played;
  }

  let updatedData = data;

  const timestamp = DateTime.fromISO(tierList?.timestamp ?? "").toRelative();
  const pageDescription =
    encounterType === "pvp"
      ? `The only WoW PvP Tier List you need. Always up-to-date, U.GG brings you the best ${EXPANSION} PvP ${getFormattedRole(
          role,
        )} Rankings and Tier List for WoW Patch ${PATCH}.`
      : `The only WoW Tier List you need. Always up-to-date, U.GG brings you the best ${EXPANSION} ${getFormattedRole(
          role,
        )} Rankings and Tier List for ${getSeoContentEncounter(encounterType)} in WoW Patch ${PATCH}.`;

  const metaDescription = `The only WoW ${getSeoContentEncounterTwo(
    encounterType,
  )} Tier List you need. Always up-to-date, U.GG brings you the best ${EXPANSION} ${getSeoContentEncounter(
    encounterType,
  )} ${getFormattedRole(role)} Rankings and Tier List for WoW Patch ${PATCH}.`;

  return (
    <div className="wow-layout-container wow-tier-lists-content-container">
      <Helmet>
        <title>
          {EXPANSION} {getFormattedRole(role)} Rankings and {getFormattedContentType(encounterType)} Tier List
        </title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      <div className="image-wrap" />
      <img className="background-image" src="https://static.bigbrain.gg/wow/background-images/uggwow-bg-tierlist.png" />
      {/* <Banner /> */}
      <div className="wow-base-content">
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail isClient>
          <div className="flex-1">
            <RampLeftRail className="sticky top-[calc(var(--header-height)+24px)]" />
          </div>
        </MediaQuery>
        <div className="wow-content wow-tier-lists-page">
          <div className="tier-lists-header">
            <h1 className="title">
              {encounterType === "pvp"
                ? `${EXPANSION} PvP Tier List and ${getFormattedRole(role)} Rankings`
                : `${EXPANSION} ${getFormattedRole(role)} Rankings and ${getFormattedContentType(encounterType)} Tier List`}
            </h1>
            {/* Hidden (not removed) while loading so the header keeps its height, matching LoL's TierListHeader */}
            <div className="details-container" style={tierListIsLoading ? { visibility: "hidden" } : undefined}>
              <div className="description">{pageDescription}</div>
              <DataDetails
                numLogsTitle={encounterType === "pvp" ? "Total Matches" : "Total Parses"}
                encounterType={encounterType}
                timestamp={timestamp}
                parses={parses}
                useDetailedTitle={true}
                isTierList={true}
              />
            </div>
          </div>
          <TierListFilters role={role} encounterType={encounterType} defaultParams={defaultParams} />
          {tierListIsLoading ? (
            <TierListTableSkeleton />
          ) : hasData && hasTalentsData ? (
            <>
              {encounterType === "pvp" ? (
                <PvpTierListsTable data={updatedData} talents={talents} role={role} encounterType={encounterType} stat={stat} />
              ) : (
                <TierListsTable
                  data={updatedData}
                  talents={talents}
                  role={role}
                  encounterType={encounterType}
                  stat={stat}
                  hscombined={hscombined}
                />
              )}
              {encounterType !== "pvp" && <TierListCrossLink encounterType={encounterType} role={role} />}
              <TierListsGrid data={updatedData} role={role} encounterType={encounterType} />
            </>
          ) : (
            <NoData />
          )}
        </div>
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail isClient>
          <div className="flex-1">
            <RampRightRail className="sticky top-[calc(var(--header-height)+24px)]" />
          </div>
        </MediaQuery>
      </div>
    </div>
  );
};

export default TierListsContainer;
